import PubSub from 'pubsub-js';
import { logService } from '@/common/services/logger';
import type { ScanEvent } from './interfaces/ScanEvent';
import type { ScanConfig } from './interfaces/ScanConfig';
import type { ScanOptions } from './interfaces/ScanOptions';

const LOG_PREFIX = '[scanService]';
const SCAN_EVENT_BASE_ID = Symbol('7T:Scanner.ScanningFinished');

const scanEventPubSub: PubSubJS.Base<ScanEvent, symbol> = PubSub;
const scanConfig: ScanConfig = {
  threshold: 30,
  prefix: 'space',
  regex: {
    pattern: /^[A-Za-z0-9.\-%]$/,
    flags: 'i',
  },
};
const scanResult = {
  code: '',
  finished: false,
};
const previousKey: {
  receivedAt: number | null,
  event: KeyboardEvent | null
} = {
  receivedAt: null,
  event: null,
};

/**
 * Time current key is received.
 */
let currentTime = 0;
/**
 * Time when previous event was received.
 */
let previousEventReceived = Date.now();
let difference = 0;
let scanEventId = SCAN_EVENT_BASE_ID;

const isPrefixBased = (event: KeyboardEvent) => event.code?.toLowerCase() === scanConfig.prefix;

const processKeyEvent = (e: KeyboardEvent) => {
  /**
   * Key codes whose values wont't be concatenated with ticketId (enter, shift, space, arrow down).
   */
  const whitelistedKeys = new RegExp(scanConfig.regex.pattern, scanConfig.regex.flags);
  const isPrefixTriggered = isPrefixBased(e);

  currentTime = Date.now();
  difference = currentTime - previousEventReceived;
  previousEventReceived = currentTime;
  logService.debug(`${LOG_PREFIX} Received key ${e.key}`);

  // Too much difference between characters - which means that this is not scan mode.
  // First time, difference is equal to current time, so we will extract that from check.
  // We also prevent scan trigger if someone holds key (e.repeat).
  // Last flag is when space is triggered as first char, we want to enter in scan mode
  if ((difference > scanConfig.threshold
    && difference !== currentTime
    && !isPrefixTriggered)
    || e.repeat) {
    logService.debug(`${LOG_PREFIX} It's not scan mode. Resetting...`, {
      key: e.key,
      keyCode: e.keyCode,
      difference,
      currentTime,
      previousEventReceived,
    });
    scanResult.code = '';
    previousKey.receivedAt = currentTime;
    previousKey.event = e;
    scanResult.finished = false;

    return scanResult;
  }

  // let's add previous char to list of scanned codes if time passed
  // before previous and current is below threshold
  // this will happen if scan barcode without space prefix
  if (scanResult.code.length === 0
    && !isPrefixTriggered
    // eslint-disable-next-line max-len
    && (previousKey.event && (currentTime - (previousKey?.receivedAt || 0)) < (scanConfig.threshold * 2))
    && whitelistedKeys.test(previousKey.event.key)
  ) {
    scanResult.code += previousKey.event.key;
    previousKey.event = null;
    previousKey.receivedAt = 0;
  }

  // do not prevent prefix event so space is entered in input
  // e.g. if user has username with space we want to allow that
  if (isPrefixTriggered) {
    logService.debug(`${LOG_PREFIX} Prefix triggered scan mode`, { key: e.key, code: e.code });
  } else {
    e.preventDefault();
    e.stopImmediatePropagation();
    e.stopPropagation();
  }

  /**
   * We need move focus out of any input to body so we don't enter codes from scanner
   * but we don't know if first char is from scan so don't move focus when first code is entered.
   * */
  if (
    scanResult.code.length !== 0
    && window.document.activeElement?.tagName.toLocaleLowerCase() !== 'body'
    && window.document.activeElement instanceof HTMLElement
  ) {
    window.document.activeElement.blur();
  }

  if (whitelistedKeys.test(e.key)) {
    scanResult.code += e.key;
    previousKey.event = null;
  }

  if (e.key.toLowerCase() === 'enter') {
    previousEventReceived = 0;

    if (scanResult.code.length) {
      scanResult.finished = true;
    }
  }

  return scanResult;
};

const emitScanEvent = (scanEventData: ScanEvent) => {
  scanEventPubSub.publish(scanEventId, scanEventData);
};

const handleTimeBasedScanning = (event: KeyboardEvent) => {
  const result = processKeyEvent(event);

  if (result.finished) {
    const eventData = {
      callSign: {
        suffix: '',
      },
      type: 'barcode',
      data: {
        code: result.code,
      },
    };

    emitScanEvent(eventData);
    scanResult.code = '';
    scanResult.finished = false;
  }
};

const getScanEventId = () => scanEventId;

const setScanEventId = (scanId: string | null) => {
  scanEventId = scanId ? Symbol(`7T:Scanner.ScanningFinished_${scanId}`) : SCAN_EVENT_BASE_ID;
};

/**
 * Call registerScanEventListener() to unsubscribe from the scan listener.
 */
const registerScanEventListener = (
  subscriptionListener: PubSubJS.SubscriptionListener<ScanEvent>,
  options?: ScanOptions,
) => {
  const { id } = options || {};
  if (id) {
    setScanEventId(id);
  }

  const scanListener = scanEventPubSub.subscribe(scanEventId, subscriptionListener);

  return () => {
    setScanEventId(null);
    return scanEventPubSub.unsubscribe(scanListener);
  };
};

const listenForScannerEvents = () => {
  window.addEventListener('keydown', handleTimeBasedScanning, true);

  return () => {
    window.removeEventListener('keydown', handleTimeBasedScanning, true);
  };
};

export {
  getScanEventId,
  setScanEventId,
  scanEventPubSub,
  emitScanEvent,
  registerScanEventListener,
  listenForScannerEvents,
};
